import { useRef } from 'react';
import classNames from 'classnames';
import { AvatarBadge, AvatarBadgeSize } from '@components/avatar/AvatarBadge';
import { AvatarContainer, AvatarContainerSize } from '@components/avatar/AvatarContainer';
import { ImageAvatar, ImageAvatarProps } from '@components/avatar/ImageAvatar';
import { IconName } from '@components/icon/Icon';
import './InteractiveAvatar.scss';

export type InteractiveAvatarSize = Exclude<AvatarContainerSize, '3xs' | '4xs'>;

const VALID_BADGE_SIZES = [
  'large',
  'medium',
  'small',
  'xs',
] as const satisfies readonly AvatarBadgeSize[];

const isValidBadgeSize = (size: InteractiveAvatarSize): size is AvatarBadgeSize => {
  return (VALID_BADGE_SIZES as readonly string[]).includes(size);
};

export interface InteractiveAvatarProps extends Omit<ImageAvatarProps, 'size'> {
  badgeIcon?: IconName;
  badgeIconSize?: AvatarBadgeSize;
  children?: React.ReactNode;
  disableGrowEffect?: boolean;
  disabled?: boolean;
  label?: string;
  overlayContent?: string | React.ReactNode;
  overlayMode?: 'hover' | 'on';
  size?: InteractiveAvatarSize;
}

export const InteractiveAvatar: React.FC<InteractiveAvatarProps> = ({
  alt,
  badgeIcon,
  badgeIconSize,
  children,
  disableGrowEffect,
  disabled,
  fill,
  isKids,
  kidsLabel,
  label,
  overlayContent,
  overlayMode,
  size = 'large',
  src,
  lazy,
  lazyProps,
  onClick,
  onKeyDown,
  tabIndex = 0,
  className,
  ...rest
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);

    if (disabled || e.defaultPrevented) return;

    if (e.code === 'Enter' || e.code === 'Space') {
      if (e.code === 'Space') {
        e.preventDefault();
      }
      ref?.current?.click();
    }
  };

  const contentClassName = classNames(
    'vds-interactive-avatar__content',
    fill && 'vds-interactive-avatar__content--fill',
  );

  const content = (
    <>
      {src ? (
        <ImageAvatar
          alt={alt}
          className={contentClassName}
          isKids={isKids}
          kidsLabel={kidsLabel}
          fill={fill}
          size={size}
          src={src}
          lazy={lazy}
          lazyProps={lazyProps}
        />
      ) : (
        <AvatarContainer className={contentClassName} fill={fill} size={size} />
      )}

      {overlayContent && !disabled && (
        <div
          className={
            overlayMode === 'hover'
              ? 'vds-interactive-avatar__overlay-content-container'
              : 'vds-interactive-avatar__content-children-wrapper'
          }>
          {overlayContent}
        </div>
      )}

      {children && (
        <div className="vds-interactive-avatar__content-children-wrapper">{children}</div>
      )}

      {badgeIcon && (
        <AvatarBadge
          className="vds-interactive-avatar__badge"
          iconName={badgeIcon}
          size={badgeIconSize ?? (isValidBadgeSize(size) ? size : 'xs')}
        />
      )}
    </>
  );

  return (
    <div
      className={classNames(
        'vds-interactive-avatar',
        `vds-interactive-avatar--${size}`,
        className,
        {
          'vds-interactive-avatar--fill': fill,
          'vds-interactive-avatar--disabled': disabled,
          'vds-interactive-avatar--overlay': overlayMode === 'hover' && !disabled,
          'vds-interactive-avatar--overlayed': overlayMode === 'on' && !disabled,
          [`vds-interactive-avatar--gap-${size}`]: label,
        },
      )}
      {...rest}
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : tabIndex}
      role="button"
      ref={ref}>
      <div
        className={classNames('vds-interactive-avatar__content-container', {
          'vds-interactive-avatar__content-container--grow': !disableGrowEffect && !disabled,
        })}>
        {content}
      </div>
      {label && <span className="vds-interactive-avatar__label">{label}</span>}
    </div>
  );
};
